// DeviceList.styles.js
// Usage in component:


const styles = (theme) => {
  // Breakpoints - use MUI's breakpoint functions which return proper media query strings
  const downXs = theme?.breakpoints?.down?.("xs") || "@media (max-width:360px)";
  const downSm = theme?.breakpoints?.down?.("sm") || "@media (max-width:600px)";
  const upSm = theme?.breakpoints?.up?.("sm") || "@media (min-width:600px)";
  const upMd = theme?.breakpoints?.up?.("md") || "@media (min-width:900px)";
  const upLg = theme?.breakpoints?.up?.("lg") || "@media (min-width:1200px)";

  const sp = (n) => (theme ? theme.spacing(n) : 8 * n);

  // Palette fallbacks
  const paper = theme ? theme.palette.background.paper : "#fff";
  const surface = theme ? theme.palette.background.default : "#f7f7f8";
  const divider = theme ? theme.palette.divider : "#e5e7eb";
  const textSub = theme ? theme.palette.text.secondary : "#6b7280";
  const ok = theme ? theme.palette.success.main : "#81c241";
  const warn = theme ? theme.palette.error.main : "#333333";

  return {
    /* Page */
    container: {
      padding: `calc(env(safe-area-inset-top, 0px) + ${sp(2)}) ${sp(2)} ${sp(2)} ${sp(2)}`,
      background: surface,
      [downSm]: { padding: `calc(env(safe-area-inset-top, 0px) + ${sp(1.5)}) ${sp(1.5)} ${sp(1.5)} ${sp(1.5)}` },
      [upLg]: { padding: `${sp(3)} ${sp(4)}` },
    },

    /* Header */
    headerWrap: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: sp(1.5),
      marginBottom: sp(2),
      [downSm]: { flexDirection: "column", alignItems: "stretch", gap: sp(1) },
      [upLg]: { marginBottom: sp(2.5) },
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: sp(1),
      flexWrap: "wrap",
      minWidth: 0,
      [downSm]: { flexDirection: "column", alignItems: "flex-start", gap: sp(0.75) },
    },
    title: {
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      gap: sp(1),
      minWidth: 0,
      fontSize: "clamp(16px, 2.2vw, 20px)",
    },
    deviceCount: {
      background: theme ? theme.palette.action.hover : "#e6e6e6",
      borderRadius: 12,
      padding: "3px 8px",
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1,
      whiteSpace: "nowrap",
      [upSm]: { fontSize: 13, padding: "4px 8px" },
    },
    bulkRow: {
      display: "flex",
      alignItems: "center",
      gap: sp(1),
      flexWrap: "wrap",
      [downSm]: { gap: sp(0.75) },
    },
    addBtn: {
      alignSelf: "flex-start",
      [downSm]: { width: "100%" },
    },

    /* List */
    list: { width: "100%" },

    row: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: sp(1.5),
      padding: sp(1.25),
      minHeight: 68,
      background: paper,
      borderBottom: `1px dashed ${divider}`,
      [downSm]: {
        display: "grid",
        gridTemplateColumns: "44px 1fr auto",
        gridTemplateAreas: `
            "check name actions"
            "check meta actions"
          `,
        columnGap: sp(1),
        rowGap: 8,
        alignItems: "center",
        padding: sp(1),
      },
      [upLg]: {
        padding: sp(1.75),
        minHeight: 76,
      },
    },
    rowHighlighted: {
      backgroundColor: theme
        ? (theme.palette.mode === "light" ? "#f4f4f9" : "rgba(255, 255, 255, 0.05)")
        : "#f4f4f9",
      borderLeft: `4px solid ${ok}`,
    },

    /* Cells */
    selectBox: {
      gridArea: "check",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 44,
      minHeight: 44, // bigger hit area on touch
      [upMd]: { marginRight: sp(0.5) },
    },

    infoWrap: {
      gridArea: "name",
      display: "flex",
      alignItems: "center",
      gap: sp(1.1),
      minWidth: 0,
      flex: 1,
      [downSm]: { gap: sp(1) },
    },
    avatar: {
      width: 44,
      height: 44,
      borderRadius: 10,
      objectFit: "cover",
      flexShrink: 0,
      [downSm]: { width: 40, height: 40 },
      [upLg]: { width: 48, height: 48 },
    },
    nameBlock: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    deviceName: {
      fontWeight: 700,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 260,
      [downSm]: { maxWidth: "100%" },
      [upLg]: { maxWidth: 360 },
    },
    deviceType: {
      color: textSub,
      fontSize: "0.82rem",
      [downSm]: { fontSize: "0.78rem" },
    },

    /* Meta */
    metaWrap: {
      gridArea: "meta",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      gap: sp(0),
      flexWrap: "nowrap",
      minWidth: 0,
      [downSm]: {
        justifyContent: "flex-start",
        flexWrap: "wrap",
        gap: sp(0.75),
      },
      [downXs]: {
        // On ultra-narrow screens, keep meta in a single row to avoid tall cards
        flexWrap: "nowrap",
        overflow: "hidden",
      },
    },
    metaPill: {
      display: "flex",
      alignItems: "center",
      gap: sp(0.2),
      minWidth: 88,
      justifyContent: "center",
      [downSm]: { minWidth: "auto", justifyContent: "flex-start" },
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        [downSm]: { fontSize: 18 },
      },
      // Hide the meta text on ultra-narrow screens to prevent wrapping
      [downXs]: {
        "& .MuiTypography-root": { display: "none" },
      },
    },
    lockIcon: {
      fontSize: 20,
      "&.locked": { color: warn },
      "&.unlocked": { color: ok },
      [downSm]: { fontSize: 18 },
    },
    batteryOk: { fontSize: 20, color: ok, [downSm]: { fontSize: 18 } },
    batteryLow: { fontSize: 20, color: "#ff0000", [downSm]: { fontSize: 18 } },
    dotOnline: { fontSize: 12, color: "#81c241" },

    /* Actions */
    actionsRight: {
      display: "flex",
      alignItems: "center",
      gap: sp(1),
      minWidth: 120, // prevents layout shift as menu appears on desktop
      justifyContent: "flex-end",
    },
    actionsInlineMobile: {
      gridArea: "actions",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: sp(0.75),
      "& .MuiButton-root": {
        paddingInline: 8,
        minHeight: 36,
        textTransform: "none",
      },
      "& .MuiIconButton-root .MuiSvgIcon-root": {
        fontSize: 20,
      },
    },
    transferBtn: { paddingInline: 8, minHeight: 36 },
    transferBtnCard: { marginLeft: "10px", paddingInline: 0 },
    menuBtn: { marginLeft: "auto" },

    /* Helper card */
    cardNote: {
      marginBottom: sp(2),
      padding: sp(1.25),
      borderRadius: 8,
      background: "#f4f4f9",
      [downSm]: { padding: sp(1) },
    },

    /* Motion preference */
    "@media (prefers-reduced-motion: reduce)": {
      "*": { transition: "none !important", animation: "none !important" },
    },
  };
};

export default styles;
